import React, { useState, useEffect, useRef } from 'react';
import { containerClass, Eyebrow, leadClass } from './ui';
import Picture, { getImageEntry } from './Picture';

// Simple SVG Icons as fallback
const XMarkIcon = ({ className }: { className?: string }) => (
  <svg aria-hidden="true" className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const PlayIcon = ({ className }: { className?: string }) => (
  <svg aria-hidden="true" className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z" />
  </svg>
);

const ChevronLeftIcon = ({ className }: { className?: string }) => (
  <svg aria-hidden="true" className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
  </svg>
);

const ChevronRightIcon = ({ className }: { className?: string }) => (
  <svg aria-hidden="true" className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
  </svg>
);

interface MediaItem {
  id: string;
  type: 'image' | 'video';
  src: string;
  alt: string;
}

const mediaItems: MediaItem[] = [
  { id: '1', type: 'image', src: '/content/WhatsApp_Image_2025-05-27_at_11.56.59.jpeg', alt: 'Vereinsaktivitäten - Bild 1' },
  { id: '2', type: 'image', src: '/content/WhatsApp_Image_2025-05-27_at_11.59.11.jpg', alt: 'Vereinsaktivitäten - Bild 2' },
  { id: '4', type: 'image', src: '/content/WhatsApp_Image_2025-05-27_at_11.59.12.jpeg', alt: 'Vereinsaktivitäten - Bild 4' },
  { id: '5', type: 'image', src: '/content/WhatsApp_Image_2025-05-27_at_12.00.07.jpeg', alt: 'Vereinsaktivitäten - Bild 5' },
  { id: '6', type: 'image', src: '/content/WhatsApp_Image_2025-05-27_at_12.04.44.jpeg', alt: 'Vereinsaktivitäten - Bild 6' },
  { id: '7', type: 'image', src: '/content/WhatsApp_Image_2025-05-27_at_12.19.27.jpeg', alt: 'Vereinsaktivitäten - Bild 7' },
  { id: '8', type: 'image', src: '/content/WhatsApp_Image_2025-05-27_at_12.20.01.jpeg', alt: 'Vereinsaktivitäten - Bild 8' },
  { id: '9', type: 'image', src: '/content/WhatsApp_Image_2025-05-27_at_12.21.12.jpeg', alt: 'Vereinsaktivitäten - Bild 9' },
  { id: '10', type: 'image', src: '/content/WhatsApp_Image_2025-05-27_at_12.40.11.jpeg', alt: 'Vereinsaktivitäten - Bild 10' },
  { id: '11', type: 'image', src: '/content/WhatsApp_Image_2025-05-27_at_12.40.12.jpeg', alt: 'Vereinsaktivitäten - Bild 11' },
  { id: '12', type: 'video', src: '/content/WhatsApp_Video_2025-05-27_at_12.00.33.mp4', alt: 'Vereinsaktivitäten - Video 1' },
  { id: '13', type: 'video', src: '/content/WhatsApp_Video_2025-05-27_at_12.01.04.mp4', alt: 'Vereinsaktivitäten - Video 2' },
  { id: '14', type: 'image', src: '/content/WhatsApp_Image_2025-05-27_at_13.29.51.jpg', alt: 'Vereinsaktivitäten - Bild 12' },
  { id: '15', type: 'video', src: '/content/WhatsApp_Video_2025-05-26_22-29-15_3.mp4', alt: 'Vereinsaktivitäten - Video 3' },
  { id: '16', type: 'video', src: '/content/WhatsApp_Video_2025-05-26_22-29-15_4.mp4', alt: 'Vereinsaktivitäten - Video 4' },
  { id: '17', type: 'video', src: '/content/WhatsApp_Video_2025-05-26_22-31-23_1.mp4', alt: 'Vereinsaktivitäten - Video 5' },
];

// Videos zeigen im Raster ein Vorschaubild (public/content/posters); die Videodatei lädt erst in der Lightbox
const thumbnailSrc = (item: MediaItem) =>
  item.type === 'video' ? item.src.replace('/content/', '/content/posters/').replace(/\.mp4$/, '.jpg') : item.src;

// Zellen sind quadratisch: Querformate füllen sie über die Höhe und brauchen entsprechend mehr Breite
const thumbnailSizes = (src: string) => {
  const entry = getImageEntry(src);
  const factor = entry ? Math.max(1, entry.width / entry.height) : 1;
  return `(min-width: 1024px) ${Math.ceil(200 * factor)}px, (min-width: 640px) ${Math.ceil(25 * factor)}vw, ${Math.ceil(34 * factor)}vw`;
};

const lightboxButtonClass =
  'absolute z-10 grid h-11 w-11 place-items-center bg-gray-900/60 text-white transition-colors hover:bg-gray-900/90';

const MediaGallery: React.FC = () => {
  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const openLightbox = (media: MediaItem) => {
    const index = mediaItems.findIndex((item) => item.id === media.id);
    setCurrentIndex(index);
    setSelectedMedia(media);
  };

  const closeLightbox = () => {
    setSelectedMedia(null);
    setCurrentIndex(0);
  };

  const goToNext = () => {
    const nextIndex = (currentIndex + 1) % mediaItems.length;
    setCurrentIndex(nextIndex);
    setSelectedMedia(mediaItems[nextIndex]);
  };

  const goToPrevious = () => {
    const prevIndex = currentIndex === 0 ? mediaItems.length - 1 : currentIndex - 1;
    setCurrentIndex(prevIndex);
    setSelectedMedia(mediaItems[prevIndex]);
  };

  // Touch handlers for swipe functionality
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    if (distance > 50) {
      goToNext();
    }
    if (distance < -50) {
      goToPrevious();
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!selectedMedia) return;

      if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'Escape') {
        closeLightbox();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMedia, currentIndex]);

  // Fokus in die geöffnete Lightbox setzen
  useEffect(() => {
    if (selectedMedia) closeButtonRef.current?.focus();
  }, [selectedMedia]);

  return (
    <section aria-labelledby="galerie-titel" className={`${containerClass} py-10 lg:py-16`}>
      <div className="mb-6 lg:mb-8 lg:grid lg:grid-cols-[5fr_7fr] lg:items-end lg:gap-16">
        <div>
          <Eyebrow>Medien Galerie</Eyebrow>
          <h2
            id="galerie-titel"
            className="text-[32px] font-extrabold leading-[1.05] tracking-[-0.02em] lg:text-[40px] lg:leading-none lg:tracking-[-0.025em]"
          >
            Impressionen aus unserem Vereinsleben
          </h2>
        </div>
        <p className={`${leadClass} mt-3 lg:mt-0`}>Entdecken Sie die Vielfalt unserer Aktivitäten in Bildern und Videos</p>
      </div>

      <ul className="grid grid-cols-3 gap-0.5 border-t-2 border-gray-900 bg-gray-200 pt-0.5 sm:grid-cols-4 lg:grid-cols-6">
        {mediaItems.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              onClick={() => openLightbox(item)}
              aria-label={`${item.alt} vergrößert anzeigen`}
              className="group relative block aspect-square w-full overflow-hidden bg-gray-900"
            >
              <Picture
                src={thumbnailSrc(item)}
                alt=""
                sizes={thumbnailSizes(thumbnailSrc(item))}
                className="h-full w-full object-cover transition-opacity duration-300 group-hover:opacity-80"
              />
              {item.type === 'video' && (
                <span className="absolute inset-0 grid place-items-center bg-gray-900/30">
                  <PlayIcon className="h-8 w-8 text-white md:h-12 md:w-12" />
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>

      {selectedMedia && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={selectedMedia.alt}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-900/95"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="relative h-full max-h-full w-full max-w-4xl p-4">
            <button ref={closeButtonRef} type="button" onClick={closeLightbox} aria-label="Schließen" className={`${lightboxButtonClass} right-4 top-4`}>
              <XMarkIcon className="h-6 w-6" />
            </button>

            {/* Navigation Arrows - Hidden on Mobile, Visible on Desktop */}
            <button
              type="button"
              onClick={goToPrevious}
              aria-label="Vorheriges Medium"
              className={`${lightboxButtonClass} left-4 top-1/2 hidden -translate-y-1/2 md:grid`}
            >
              <ChevronLeftIcon className="h-6 w-6" />
            </button>
            <button
              type="button"
              onClick={goToNext}
              aria-label="Nächstes Medium"
              className={`${lightboxButtonClass} right-4 top-1/2 hidden -translate-y-1/2 md:grid`}
            >
              <ChevronRightIcon className="h-6 w-6" />
            </button>

            <div className="flex h-full items-center justify-center">
              {selectedMedia.type === 'image' ? (
                <Picture
                  src={selectedMedia.src}
                  alt={selectedMedia.alt}
                  sizes="(min-width: 896px) 864px, 100vw"
                  loading="eager"
                  className="max-h-full max-w-full object-contain"
                />
              ) : (
                <video src={selectedMedia.src} poster={thumbnailSrc(selectedMedia)} controls autoPlay className="max-h-full max-w-full">
                  Ihr Browser unterstützt das Video-Element nicht.
                </video>
              )}
            </div>

            <div className="absolute bottom-4 left-1/2 z-10 -translate-x-1/2 bg-gray-900/60 px-3 py-1 text-sm text-white">
              {currentIndex + 1} / {mediaItems.length}
            </div>

            <div className="absolute bottom-16 left-1/2 z-10 -translate-x-1/2 text-xs text-white opacity-70 md:hidden">
              ← Wischen zum Navigieren →
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default MediaGallery;
